import { Button, Dropdown, MenuProps } from "antd";
import { Radio, RadioChangeEvent, Row } from "antd";
import { useCallback, useMemo } from "react";
import { MoreOutlined, EditOutlined } from "@ant-design/icons";
import { SingleSelect } from "../../../components/SingleSelect";
import { CreateButton } from "./CreateEmails/CreateButton";
import { useDeleteAll } from "../hooks/useDeleteAll";
import { SendAllButton } from "./SendAllButton";
import { Email } from "../../../types/Email";
import { usePermissions } from "../../../lib/hooks/usePermissions";
import { IOption } from "../../../types/Option";
import { useSelector } from "react-redux";
import { NavigateFunction, useNavigate } from "react-router-dom";
import ROUTES from "../../../configs/routesConfig";

type HeaderProps = {
  request: any;
  isLoading: boolean;
  setRequest: React.Dispatch<React.SetStateAction<any>>;
  reloadData: () => void;
  setData: React.Dispatch<React.SetStateAction<Email[]>>;
};

export const Header = ({ request, isLoading, setRequest, reloadData, setData }: HeaderProps) => {
  const { removeAll } = useDeleteAll(reloadData);

  const navigate: NavigateFunction = useNavigate();

  const metaData = useSelector((state: any) => state.metaData);

  const { hasAccessByController, hasAccess } = usePermissions("MailingEmails");

  const navigateToBulkCreation = useCallback(() => {
    navigate(`${ROUTES.EMAILS_BULK}`);
  }, [navigate]);

  const typeOptions: IOption[] = useMemo(() => {
    const values: IOption[] = [];

    values.push({ value: 0, label: "Outbox" });

    if (metaData.featureFlags.ScheduledEmails) {
      values.push({ value: 2, label: "Scheduled" });
      values.push({ value: 4, label: "Recurring" });
    }

    values.push({ value: 5, label: "Sending" });
    values.push({ value: 1, label: "Sent" });

    return values;
  }, [metaData]);

  const dropdownItems: MenuProps["items"] = useMemo(
    () => [
      {
        key: "1",
        label: <Row onClick={removeAll}>Delete All from Outbox</Row>
      }
    ],
    [removeAll]
  );

  const sentFilterOptions = useMemo(
    () => [
      { label: "Today", value: 1 },
      { label: "Last Week", value: 2 },
      { label: "Last Month", value: 3 },
      { label: "Last 6 Months", value: 4 }
    ],
    []
  );

  const onFilterChange = useCallback(
    (value: number) => {
      setRequest((prev: any) => ({ ...prev, dateType: value, pageNumber: 1 }));
    },
    [setRequest]
  );

  const onTypeChange = useCallback(
    (e: RadioChangeEvent) => {
      setData([]);
      const page = e.target.value;

      const request: any = {
        pageNumber: 1,
        pageSize: 50,
        page
      };

      if (page === 1) {
        request.dateType = 4;
      }

      setRequest(request);
    },
    [setRequest, setData]
  );

  return (
    <Row className="d-block w-100 mx-8 header__container">
      <Row className="my-6">
        <h3 className="h h3">Emails</h3>
      </Row>
      <Row className="justify-space-between">
        <Radio.Group
          optionType="button"
          options={typeOptions}
          defaultValue={0}
          onChange={onTypeChange}
          disabled={isLoading}
        />
        {request.page === 0 && (
          <Row>
            {hasAccessByController("DeleteAll") && (
              <Dropdown className="mr-2" menu={{ items: dropdownItems }} trigger={["click"]} disabled={isLoading}>
                <Button shape="circle" icon={<MoreOutlined />}></Button>
              </Dropdown>
            )}
            {hasAccessByController("SendAll") && <SendAllButton isLoading={isLoading} reloadData={reloadData} />}
            {hasAccess("MailingsCreateBulk", "Add") && (
              <Button
                icon={<EditOutlined />}
                shape="round"
                className="mr-2"
                disabled={isLoading}
                onClick={() => navigateToBulkCreation()}
              >
                Compose
              </Button>
            )}
            {hasAccessByController("CreateDaily") && <CreateButton isLoading={isLoading} reloadData={reloadData} />}
          </Row>
        )}

        {request.page === 1 && (
          <SingleSelect
            options={sentFilterOptions}
            label="Sent"
            value={request.dateType}
            onChange={onFilterChange}
            loading={isLoading}
          />
        )}
      </Row>
    </Row>
  );
};
